import { useEffect, useMemo, useState } from "react";
import { Section, NumField, ResultRow, PrimaryButton, SecondaryButton, SaveButton } from "@/components/CalcUI";
import { fetchSpecs } from "@/lib/api";
import { localCalc } from "@/lib/calc";
import { CheckCircle2, XCircle, ArrowRight, ArrowLeft, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Setpoints", "Loads", "Selection", "Verify"];

export default function CasingWizard() {
  const [step, setStep] = useState(0);
  const [inp, setInp] = useState({
    setting_depth: "10000",
    mw: "10.0",
    formation_pressure_ppg: "13.0",
    frac_grad_ppg: "16.0",
    sf_burst: "1.1",
    sf_collapse: "1.125",
    sf_tension: "1.6",
    weight_ppf: "0",
    section_type: "casing",
  });
  const [pool, setPool] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchSpecs(inp.section_type).then((d) => setPool(d)).catch(() => setPool([]));
  }, [inp.section_type]);

  const num = (k) => parseFloat(inp[k]) || 0;

  // ---------- Load calculation (Step 2) ----------
  const loads = useMemo(() => {
    const depth = num("setting_depth");
    const mw = num("mw");
    const fp_ppg = num("formation_pressure_ppg");
    const frac = num("frac_grad_ppg");
    // Burst: worst case = full formation pressure at surface minus min backup (dry gas assumption)
    const burst_load = fp_ppg * 0.052 * depth; // psi (max at surface, dry gas conservative)
    // Collapse: worst case = full external mud column with empty pipe
    const collapse_load = mw * 0.052 * depth; // psi
    // Axial tension: buoyed weight of the string. Using w in lb/ft * depth * buoyancy factor
    const buoy = 1 - mw / 65.5;
    const tension_load = num("weight_ppf") * depth * buoy; // lbf
    // Frac at shoe (informational)
    const frac_shoe = frac * 0.052 * depth;
    return { burst_load, collapse_load, tension_load, frac_shoe, buoy };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- num() only reads inp
  }, [inp]);

  // Required min ratings with SF
  const required = useMemo(
    () => ({
      burst_min: loads.burst_load * (parseFloat(inp.sf_burst) || 0),
      collapse_min: loads.collapse_load * (parseFloat(inp.sf_collapse) || 0),
      tension_min: loads.tension_load * (parseFloat(inp.sf_tension) || 0),
    }),
    [loads, inp.sf_burst, inp.sf_collapse, inp.sf_tension]
  );

  // ---------- Candidates (Step 3) ----------
  const candidates = useMemo(() => {
    if (!pool.length) return [];
    return pool
      .map((p) => {
        // Estimate joint strength from wall+od+yield (approx cross-section * yield)
        // A = pi/4 * (OD² - ID²)
        const A = (Math.PI / 4) * (p.od * p.od - p.id * p.id);
        const joint_strength = A * (p.yield || 0); // lbf (simplified)
        const burstOk = (p.burst || 0) >= required.burst_min;
        const collapseOk = (p.collapse || 0) >= required.collapse_min;
        const tensionOk = joint_strength >= required.tension_min;
        return { ...p, joint_strength, burstOk, collapseOk, tensionOk, allOk: burstOk && collapseOk && tensionOk };
      })
      .sort((a, b) => (b.allOk - a.allOk) || (a.weight - b.weight));
  }, [pool, required]);

  const verify = useMemo(() => {
    if (!selected) return null;
    const A = (Math.PI / 4) * (selected.od * selected.od - selected.id * selected.id);
    const joint_strength = A * (selected.yield || 0);
    return {
      burst: {
        pass: selected.burst >= required.burst_min,
        rating: selected.burst,
        required: Math.round(required.burst_min),
        margin: Math.round(((selected.burst - loads.burst_load) / loads.burst_load) * 100),
      },
      collapse: {
        pass: selected.collapse >= required.collapse_min,
        rating: selected.collapse,
        required: Math.round(required.collapse_min),
        margin: Math.round(((selected.collapse - loads.collapse_load) / loads.collapse_load) * 100),
      },
      tension: {
        pass: joint_strength >= required.tension_min,
        rating: Math.round(joint_strength),
        required: Math.round(required.tension_min),
        margin: Math.round(((joint_strength - loads.tension_load) / Math.max(loads.tension_load, 1)) * 100),
      },
    };
  }, [selected, required, loads]);

  const overallPass = verify && verify.burst.pass && verify.collapse.pass && verify.tension.pass;

  // ---------- Steps ----------
  const canNext = step < STEPS.length - 1;
  const canPrev = step > 0;

  const setField = (k, v) => setInp({ ...inp, [k]: v });

  return (
    <div data-testid="casing-wizard-page">
      <Section
        title="Casing Design Wizard"
        subtitle="Step through well data → load calcs → pipe selection → verification. Builds a defensible casing tally in minutes using the standard API 5CT spec library."
      >
        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <button
                data-testid={`step-${i}`}
                onClick={() => (i < step ? setStep(i) : null)}
                className={`px-3 py-1.5 mono text-xs tracking-widest uppercase border ${
                  i === step
                    ? "bg-[#f59e0b] border-[#f59e0b] text-[#0b0e13]"
                    : i < step
                    ? "border-[#334155] text-[#f59e0b]"
                    : "border-[#334155] text-[#64748b]"
                }`}
              >
                {i + 1}. {s}
              </button>
              {i < STEPS.length - 1 && <ArrowRight className="w-3 h-3 text-[#334155]" />}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div data-testid="wizard-step-setpoints" className="border border-[#334155] bg-[#11151c] p-5">
            <h3 className="heading text-sm font-bold uppercase tracking-wide mb-4">Well Setpoints</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
              <NumField testid="w-depth" label="Casing setting depth" unit="ft TVD" value={inp.setting_depth} onChange={(v) => setField("setting_depth", v)} />
              <NumField testid="w-mw" label="Mud weight" unit="ppg" value={inp.mw} onChange={(v) => setField("mw", v)} />
              <NumField testid="w-fp" label="Formation pressure" unit="ppg equiv." value={inp.formation_pressure_ppg} onChange={(v) => setField("formation_pressure_ppg", v)} />
              <NumField testid="w-frac" label="Fracture gradient at shoe" unit="ppg" value={inp.frac_grad_ppg} onChange={(v) => setField("frac_grad_ppg", v)} />
              <NumField testid="w-wt" label="Est. pipe weight (guess)" unit="lb/ft" value={inp.weight_ppf} onChange={(v) => setField("weight_ppf", v)} hint="Auto-tuned in Selection" />
            </div>

            <h4 className="heading text-xs uppercase tracking-widest text-[#f59e0b] mb-2">Design Factors (SF)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              <NumField testid="sf-burst" label="Burst SF" unit="—" value={inp.sf_burst} onChange={(v) => setField("sf_burst", v)} />
              <NumField testid="sf-collapse" label="Collapse SF" unit="—" value={inp.sf_collapse} onChange={(v) => setField("sf_collapse", v)} />
              <NumField testid="sf-tension" label="Tension SF" unit="—" value={inp.sf_tension} onChange={(v) => setField("sf_tension", v)} />
            </div>

            <h4 className="heading text-xs uppercase tracking-widest text-[#f59e0b] mb-2">Pipe Class</h4>
            <div className="inline-flex border border-[#334155] mb-2">
              {["casing", "tubing", "drill-pipe"].map((k) => (
                <button
                  key={k}
                  data-testid={`w-kind-${k}`}
                  onClick={() => setField("section_type", k)}
                  className={`px-3 py-1.5 mono text-xs tracking-widest uppercase ${
                    inp.section_type === k ? "bg-[#f59e0b] text-[#0b0e13]" : "text-[#94a3b8] hover:text-[#f8fafc]"
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="wizard-step-loads" className="border border-[#334155] bg-[#11151c] p-5">
            <h3 className="heading text-sm font-bold uppercase tracking-wide mb-4">Design Loads</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="text-xs mono tracking-widest text-[#94a3b8] uppercase mb-2">Worst-case loads (unfactored)</div>
                <ResultRow label="Burst load" value={Math.round(loads.burst_load)} unit="psi" emphasis />
                <ResultRow label="Collapse load" value={Math.round(loads.collapse_load)} unit="psi" emphasis />
                <ResultRow label="Tension load" value={Math.round(loads.tension_load)} unit="lbf" emphasis />
                <ResultRow label="Buoyancy factor" value={loads.buoy.toFixed(4)} unit="—" />
                <ResultRow label="Frac P @ shoe" value={Math.round(loads.frac_shoe)} unit="psi" />
              </div>
              <div>
                <div className="text-xs mono tracking-widest text-[#f59e0b] uppercase mb-2">Required min ratings (with SF)</div>
                <ResultRow label="Burst rating ≥" value={Math.round(required.burst_min)} unit="psi" />
                <ResultRow label="Collapse rating ≥" value={Math.round(required.collapse_min)} unit="psi" />
                <ResultRow label="Joint strength ≥" value={Math.round(required.tension_min)} unit="lbf" />
                <div className="text-[10px] mono text-[#64748b] mt-3 leading-relaxed">
                  Loads follow standard casing design: full-column formation P (dry-gas burst), full external mud column (collapse with empty pipe), and buoyed hanging weight (tension). Sub-tier loads such as displacement kicks, cementing surges, and biaxial derating are not included in this MVP.
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-selection" className="border border-[#334155] bg-[#11151c] p-5">
            <h3 className="heading text-sm font-bold uppercase tracking-wide mb-4">Select a Pipe</h3>
            {candidates.length === 0 ? (
              <div className="mono text-sm text-[#94a3b8]">No spec data loaded.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full mono text-xs">
                  <thead>
                    <tr className="border-b border-[#334155] bg-[#0b0e13] uppercase tracking-widest text-[#94a3b8]">
                      <th className="text-left px-3 py-2">OD</th>
                      <th className="text-left px-3 py-2">Wt</th>
                      <th className="text-left px-3 py-2">Wall</th>
                      <th className="text-left px-3 py-2">Grade</th>
                      <th className="text-right px-3 py-2">Burst</th>
                      <th className="text-right px-3 py-2">Collapse</th>
                      <th className="text-right px-3 py-2">Joint</th>
                      <th className="text-center px-3 py-2">Meets?</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.slice(0, 30).map((c, i) => (
                      <tr
                        key={`${c.od}-${c.weight}-${c.grade}-${c.id}`}
                        data-testid={`cand-row-${i}`}
                        className={`${i % 2 === 0 ? "bg-[#0b0e13]" : "bg-[#11151c]"} ${selected === c ? "outline outline-1 outline-[#f59e0b]" : ""}`}
                      >
                        <td className="px-3 py-2 text-[#f8fafc]">{c.od}"</td>
                        <td className="px-3 py-2 text-[#94a3b8]">{c.weight}</td>
                        <td className="px-3 py-2 text-[#94a3b8]">{c.wall}</td>
                        <td className="px-3 py-2 text-[#94a3b8]">{c.grade}</td>
                        <td className="px-3 py-2 text-right text-[#f8fafc]">{c.burst}</td>
                        <td className="px-3 py-2 text-right text-[#f8fafc]">{c.collapse}</td>
                        <td className="px-3 py-2 text-right text-[#f8fafc]">{Math.round(c.joint_strength).toLocaleString()}</td>
                        <td className="px-3 py-2 text-center">
                          {c.allOk ? (
                            <CheckCircle2 className="w-4 h-4 text-[#10b981] inline" />
                          ) : (
                            <XCircle className="w-4 h-4 text-[#ef4444] inline" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            data-testid={`select-cand-${i}`}
                            onClick={() => {
                              setSelected(c);
                              setField("weight_ppf", String(c.weight));
                              toast.success(`${c.od}" ${c.weight}# ${c.grade} selected`);
                            }}
                            className={`border px-2 py-1 mono text-[10px] tracking-widest uppercase ${
                              selected === c
                                ? "bg-[#f59e0b] border-[#f59e0b] text-[#0b0e13]"
                                : "border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b]"
                            }`}
                          >
                            {selected === c ? "Selected" : "Choose"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-verify" className="border border-[#334155] bg-[#11151c] p-5">
            <h3 className="heading text-sm font-bold uppercase tracking-wide mb-4">Verify Design</h3>
            {!selected ? (
              <div className="mono text-sm text-[#94a3b8]">No pipe selected. Go back to Selection.</div>
            ) : (
              <>
                <div
                  data-testid="verify-verdict"
                  className={`flex items-center gap-3 border p-3 mb-5 ${
                    overallPass ? "border-[#10b981] bg-[#10b981]/10" : "border-[#ef4444] bg-[#ef4444]/10"
                  }`}
                >
                  {overallPass ? (
                    <CheckCircle2 className="w-8 h-8 text-[#10b981]" />
                  ) : (
                    <XCircle className="w-8 h-8 text-[#ef4444]" />
                  )}
                  <div>
                    <div className="mono text-xs tracking-widest" style={{ color: overallPass ? "#10b981" : "#ef4444" }}>
                      DESIGN VERDICT
                    </div>
                    <div className="heading text-xl font-bold" style={{ color: overallPass ? "#10b981" : "#ef4444" }}>
                      {overallPass ? "MEETS ALL DESIGN FACTORS" : "FAILS ONE OR MORE FACTORS"}
                    </div>
                  </div>
                </div>

                <div className="text-xs mono text-[#94a3b8] mb-4">
                  Selected: <span className="text-[#f59e0b] font-bold">{selected.od}" · {selected.weight} ppf · {selected.grade}</span>
                  {" · "}ID {selected.id}" · Wall {selected.wall}"
                </div>

                <div className="border border-[#334155]">
                  <table className="w-full mono text-sm">
                    <thead>
                      <tr className="border-b border-[#334155] bg-[#0b0e13] text-xs uppercase tracking-widest text-[#94a3b8]">
                        <th className="text-left px-4 py-2">Check</th>
                        <th className="text-right px-4 py-2">Rating</th>
                        <th className="text-right px-4 py-2">Required</th>
                        <th className="text-right px-4 py-2">Margin</th>
                        <th className="text-center px-4 py-2">Pass</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Burst", verify.burst, "psi"],
                        ["Collapse", verify.collapse, "psi"],
                        ["Tension", verify.tension, "lbf"],
                      ].map(([label, r, u]) => (
                        <tr key={label} className="border-b border-[#334155]/50">
                          <td className="px-4 py-2 text-[#f8fafc]">{label}</td>
                          <td className="px-4 py-2 text-right text-[#f8fafc]">{r.rating.toLocaleString()} {u}</td>
                          <td className="px-4 py-2 text-right text-[#94a3b8]">{r.required.toLocaleString()} {u}</td>
                          <td className={`px-4 py-2 text-right ${r.margin >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`}>
                            {r.margin >= 0 ? "+" : ""}{r.margin}%
                          </td>
                          <td className="px-4 py-2 text-center">
                            {r.pass ? (
                              <CheckCircle2 className="w-4 h-4 text-[#10b981] inline" />
                            ) : (
                              <XCircle className="w-4 h-4 text-[#ef4444] inline" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 flex justify-end">
                  <SaveButton
                    getPayload={() =>
                      selected && {
                        category: "Casing Design",
                        calculator: "casing_wizard",
                        inputs: { ...inp, pipe: `${selected.od}"·${selected.weight}#·${selected.grade}` },
                        outputs: {
                          selected_od: selected.od,
                          selected_weight: selected.weight,
                          selected_grade: selected.grade,
                          burst_rating: selected.burst,
                          collapse_rating: selected.collapse,
                          joint_strength: Math.round(verify.tension.rating),
                          verdict: overallPass ? "PASS" : "FAIL",
                        },
                        units: "field",
                        default_name: `Casing ${selected.od}" ${selected.grade}`,
                      }
                    }
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Step navigation */}
        <div className="mt-6 flex justify-between items-center">
          <SecondaryButton testid="wiz-prev" onClick={() => canPrev && setStep(step - 1)}>
            <span className="inline-flex items-center gap-1.5"><ArrowLeft className="w-3.5 h-3.5" /> Back</span>
          </SecondaryButton>
          {canNext ? (
            <PrimaryButton testid="wiz-next" onClick={() => setStep(step + 1)}>
              <span className="inline-flex items-center gap-1.5">Next <ArrowRight className="w-3.5 h-3.5" /></span>
            </PrimaryButton>
          ) : (
            <div className="text-xs mono text-[#94a3b8] flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-[#f59e0b]" /> Save the design to your Job History
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
